<template>
  <div class="game-view-container">
    <WaitView
      ref="waitViewInstance"
      :gameRoomcode="gameRoomcode"
      :gameState="gameState"
      :isHost="isHost"
      v-if="displayWaitView"
    />
    <transition name="fade-slow">
      <PromptTimerAnimation
        v-if="displayQuestionView"
        :duration="totalSecondsRemaining"
      />
    </transition>
    <transition name="fade">
      <div
        class="question-container"
        v-if="displayQuestionView && totalSecondsRemaining > 0"
      >
        <div class="header-info-container">
          <transition name='fade-slow'>
            <div
              v-if="activeQuestionIndex < gameState.prompts.length && timeRemaining.length > 0"
              class="prompt-index-text"
            >
              Prompt {{ activeQuestionIndex + 1 }} of {{ gameState.prompts.length }}
            </div>
          </transition>
          <transition name='fade'>
            <div
              v-if="timeRemaining.length > 0"
              :class="[ 'timer-text', { 'thirty-seconds-left': totalSecondsRemaining < 31 && totalSecondsRemaining >= 11, 'ten-seconds-left': totalSecondsRemaining < 11 && totalSecondsRemaining >= 0, 'zero-seconds-left': totalSecondsRemaining < 0 }]"
            >
              {{ timeRemaining }}
            </div>
          </transition>
        </div>
        <transition-group name="pop-from-top-delay">
          <QuestionView
            v-for="(question, questionIndex) in gameState.prompts"
            v-show="questionIndex === activeQuestionIndex"
            @answer-submitted="advanceActiveQuestionIndex"
            :key="question.promptText"
            :username="username"
            :questionIndex="questionIndex"
            :numQuestions="gameState.prompts.length"
            :gameState="gameState"
            :gameRoomcode="gameRoomcode"
            :questionData="question"
            :isSafari="isSafari"
            class="question-view-item"
          />
        </transition-group>
      </div>
    </transition>
    <transition name="pop-from-top-delay">
      <div
        class="question-container"
        v-if="displayQuestionView && isAnswerSubmissionComplete"
      >
        <div
          class="question-view-item"
        >
          <div
            class="submission-message-container"
          >
            <div>
              You're all set!
            </div>
            <div>
              Waiting for other players to submit their answers...
            </div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="pop-from-top">
      <VoteStartView
        ref="voteStartViewInstance"
        v-if="displayVoteStartView"
      />
    </transition>
    <VoteView
      ref="voteViewInstance"
      v-if="displayVoteView"
      :username="username"
      :gameRoomcode="gameRoomcode"
      :voteInfo="turnInfo"
      :primaryColor="primaryColor"
      :totalSecondsRemaining="totalSecondsRemaining"
      :timeRemaining="timeRemaining"
      :isSafari="isSafari"
      @play-sound-effect="propagateEvent('play-sound-effect', $event)"
    />
    <transition name="pop-from-top">
      <VoteResultView
        ref="voteResultViewInstance"
        v-if="displayVoteResultView"
        :voteResultInfo="previousTurnResult"
        :isSafari="isSafari"
        @play-sound-effect="propagateEvent('play-sound-effect', $event)"
      />
    </transition>
    <RoundResultView
      ref="roundResultViewInstance"
      v-show="displayRoundResultView"
      :isLastRound="isLastRound"
    />
    <GameEndView
      ref="gameEndViewInstance"
      v-if="displayGameEndView"
      :gameState="gameState"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import WaitView from '@/components/gameComponents/WaitView.vue';
import PromptTimerAnimation from '@/components/gameComponents/PromptTimerAnimation';
import QuestionView from '@/components/gameComponents/QuestionView';
import VoteStartView from '@/components/gameComponents/VoteStartView.vue';
import VoteView from '@/components/gameComponents/VoteView.vue';
import VoteResultView from '@/components/gameComponents/VoteResultView.vue';
import RoundResultView from '@/components/gameComponents/RoundResultView.vue';
import GameEndView from '@/components/gameComponents/GameEndView.vue';

export default Vue.extend({
  name: 'GameView',
  components: {
    WaitView,
    PromptTimerAnimation,
    QuestionView,
    VoteStartView,
    VoteView,
    VoteResultView,
    RoundResultView,
    GameEndView
  },
  props: {
    username: String,
    gameRoomcode: String,
    gameState: Object,
    primaryColor: String,
    isHost: Boolean,
    totalSecondsRemaining: Number,
    timeRemaining: String
  },
  data: () => ({
    displayWaitView: false,
    displayQuestionView: false,
    displayVoteStartView: false,
    displayVoteView: false,
    displayVoteResultView: false,
    displayRoundResultView: false,
    displayGameEndView: false,
    turnInfo: {
      prompt: '',
      answers: [{
        answerId: '',
        answerText: ''
      }, {
        answerId: '',
        answerText: ''
      }],
      canPlayerVote: true,
      wereAllAnswersSubmitted: true
    },
    previousTurnResult: {
      prompt: '',
      resultMsg: '',
      answers: [{
        answerId: '',
        answerText: '',
        username: '',
        primaryColor: '',
        numVotes: 0
      }, {
        answerId: '',
        answerText: '',
        username: '',
        primaryColor: '',
        numVotes: 0
      }]
    },
    activeQuestionIndex: 0,
    isSafari: false
  }),
  computed: {
    prompts: function () {
      if ('prompts' in this.gameState) {
        return this.gameState.prompts;
      }

      return [];
    },
    isAnswerSubmissionComplete: function () {
      if ('prompts' in this.gameState) {
        return this.activeQuestionIndex === this.gameState.prompts.length;
      }

      return false;
    },
    isLastRound: function () {
      if ('roundIndex' in this.gameState && 'numRounds' in this.gameState) {
        return this.gameState.roundIndex === this.gameState.numRounds;
      }

      return false;
    },
    scoreboard: function() {
      return 'scoreboard' in this.gameState ? this.gameState.scoreboard : [];
    }
  },
  methods: {
    updateTurnInfo () {
      if ('turnInfo' in this.gameState) {
        this.turnInfo = this.gameState.turnInfo;
      } else {
        this.turnInfo = {
          prompt: '',
          answers: [{
            answerId: '',
            answerText: ''
          }, {
            answerId: '',
            answerText: ''
          }]
        };  
      }
    },
    resetActiveQuestionIndex () {
      this.activeQuestionIndex = 0;
    },
    advanceActiveQuestionIndex () {
      ++this.activeQuestionIndex;
    },
    propagateEvent(eventName, eventData) {
      this.$emit(eventName, eventData);
    }
  },
  watch: {
    prompts: function () {
      this.resetActiveQuestionIndex();
    }
  },
  mounted: function() {
    this.$nextTick(function() {
      this.isSafari = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') === -1;
    });
  },
})
</script>

<style lang="scss">
@import '@/styles/game-view-styles.scss';
</style>
